import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import AdminDashboard from './AdminDashboard';

const AdminAuthWrapper = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setSigningIn(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setPassword(''); // Clear password from memory
    } catch (err) {
      console.error('Admin sign-in failed:', err);
      switch (err.code) {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Incorrect email or password');
          break;
        case 'auth/user-not-found':
          setError('No account found for that email');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts. Please try again later.');
          break;
        default:
          setError('Sign-in failed. Please try again.');
      }
      setPassword('');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Sign-out failed:', err);
    }
    navigate('/');
  };

  if (loading) {
    return <div style={{ padding: '2rem' }}>Loading...</div>;
  }

  if (!user) {
    return (
      <div style={loginContainerStyle}>
        <div style={loginBoxStyle}>
          <h2>🔒 Admin Dashboard Access</h2>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Sign in with your admin account
          </p>
          {error && (
            <div style={errorStyle}>{error}</div>
          )}
          <form onSubmit={handleSignIn}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              style={inputStyle}
              autoComplete="username"
              autoFocus
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              style={inputStyle}
              autoComplete="current-password"
            />
            <button type="submit" style={buttonStyle} disabled={signingIn}>
              {signingIn ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // User is authenticated, show the admin dashboard with logout option
  return (
    <div>
      <div style={headerStyle}>
        <span>Admin Dashboard - {user.email}</span>
        <button onClick={handleLogout} style={logoutButtonStyle}>
          Logout
        </button>
      </div>
      <AdminDashboard />
    </div>
  );
};

// Styles
const loginContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
  backgroundColor: '#f5f5f5',
};

const loginBoxStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  width: '100%',
  maxWidth: '400px',
  textAlign: 'center',
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  marginBottom: '1rem',
  border: '1px solid #ddd',
  borderRadius: '4px',
  fontSize: '1rem',
  boxSizing: 'border-box',
};

const buttonStyle = {
  width: '100%',
  padding: '0.75rem',
  backgroundColor: '#333',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  fontSize: '1rem',
  cursor: 'pointer',
};

const errorStyle = {
  color: '#d32f2f',
  backgroundColor: '#ffebee',
  padding: '0.5rem',
  borderRadius: '4px',
  marginBottom: '1rem',
  fontSize: '0.9rem',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem 2rem',
  backgroundColor: '#333',
  color: 'white',
};

const logoutButtonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: '#d32f2f',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
};

export default AdminAuthWrapper;
